import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronRight, Clock, CheckCircle, Truck, XCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
        setOrders(data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_payment':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'processing':
        return <Package className="w-5 h-5 text-[#00F0FF]" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-blue-400" />;
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-[#A1A7B5]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'processing':
        return 'text-[#00F0FF] bg-[#00F0FF]/10';
      case 'shipped':
        return 'text-blue-400 bg-blue-400/10';
      case 'delivered':
        return 'text-green-400 bg-green-400/10';
      case 'cancelled':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-[#A1A7B5] bg-[#A1A7B5]/10';
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            My Orders
          </h1>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-6">
                <div className="skeleton h-6 w-1/4 mb-4" />
                <div className="skeleton h-4 w-1/2 mb-2" />
                <div className="skeleton h-4 w-1/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Orders
        </h1>

        {orders.length === 0 ? (
          <div className="text-center py-16" data-testid="no-orders">
            <Package className="w-16 h-16 text-[#1E2532] mx-auto mb-4" />
            <p className="text-[#A1A7B5] text-lg mb-4">No orders yet</p>
            <Link to="/products">
              <Button className="btn-primary">
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className="block glass-card p-6 hover:border-[#00F0FF]/50 transition-all"
                data-testid={`order-${order.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-[#A1A7B5] text-sm">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-white font-semibold text-lg">
                      ${order.total.toFixed(2)}
                    </p>
                  </div>
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getStatusColor(order.status)}`}>
                    {getStatusIcon(order.status)}
                    <span className="text-sm font-medium">{formatStatus(order.status)}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-4 overflow-x-auto pb-2">
                  {order.items.slice(0, 4).map((item, index) => (
                    <div key={index} className="flex-shrink-0">
                      <div className="w-16 h-16 bg-[#1E2532] rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-[#A1A7B5]" />
                      </div>
                    </div>
                  ))}
                  {order.items.length > 4 && (
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-[#1E2532] rounded-lg flex items-center justify-center">
                        <span className="text-[#A1A7B5] font-medium">+{order.items.length - 4}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1E2532]">
                  <div className="text-[#A1A7B5] text-sm">
                    <span>{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(order.created_at).toLocaleDateString()}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#A1A7B5]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
